import * as focusTrap from "focus-trap";
import * as React from "react";
import { useCallback, useEffect, useRef } from "react";
import { useTranslations } from "../i18n/useTranslations";
import { ListItemWithLinkOnly } from "../Menu/ListItemWithLinkOnly";
import { useMenu } from "../Menu/useMenu";
import { useUser } from "../user/useUser";
import { labelToDashes, stopPropagation } from "../utils";
import { DDProBanner } from "./DDProBanner";
import { useUserMenu } from "./useUserMenu";

export function UserMenu() {
  const { t } = useTranslations();
  const { logoutRequest, user, isPro } = useUser();
  const { closeUserMenu } = useUserMenu();
  const { items } = useMenu();
  const { myAccount, logout } = items;

  const elRef = useRef<HTMLDivElement>();

  const onClickOnLogout = useCallback(() => {
    closeUserMenu();
    logoutRequest();
  }, [closeUserMenu, logoutRequest]);

  const onKeyDownCallback = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeUserMenu();
      }
    },
    [closeUserMenu]
  );

  useEffect(() => {
    document.addEventListener("keydown", onKeyDownCallback);
    return () => document.removeEventListener("keydown", onKeyDownCallback);
  }, [onKeyDownCallback]);

  const handleStopPropagation = useCallback(stopPropagation, [stopPropagation]);

  useEffect(() => {
    // close this menu if we click outside
    document.addEventListener("click", closeUserMenu);
    return () => document.removeEventListener("click", closeUserMenu);
  }, [closeUserMenu]);

  useEffect(() => {
    // don't close this menu if we click inside
    const el = elRef.current;
    el?.addEventListener("click", handleStopPropagation);
    return () => el?.removeEventListener("click", handleStopPropagation);
  }, [handleStopPropagation]);

  useEffect(() => {
    if (elRef.current !== null) {
      const trap = focusTrap
        .createFocusTrap(elRef.current, { fallbackFocus: elRef.current, allowOutsideClick: true })
        .activate();
      return () => void trap.deactivate();
    }
  }, []);

  return (
    <div
      role="menu"
      aria-label={t("User menu")}
      ref={elRef}
      className="elm-fixed elm-overflow-y-auto elm-max-h-[calc(100vh_-_60px)] elm-left-0 elm-bottom-0 md:elm-bottom-auto md:elm-left-auto md:elm-absolute elm-z-20 elm-top-14 md:elm-top-12 elm-bg-white elm-p-6 md:elm-p-12 md:elm-right-4 elm-w-full md:elm-w-104 md:elm-max-w-md md:elm-rounded-xl md:elm-shadow-2xl"
    >
      {isPro && <DDProBanner />}
      <div
        role="heading"
        className="elm-text-dd-gray-900 elm-text-3xl elm-py-2 elm-m-0 elm-mb-2 elm-font-semibold"
      >
        {myAccount.label}
      </div>
      <ul className="elm-p-0 elm-m-0 elm-list-none">
        {myAccount.children.map(item =>
          item.guard?.(user) === false ? null : (
            <ListItemWithLinkOnly
              shouldBeDisplayed={true}
              key={labelToDashes(item.label)}
              item={item}
              className="elm-border-0 elm-border-b elm-border-solid elm-border-gray-300"
            />
          )
        )}
        <li
          key={labelToDashes(logout.label)}
          className="elm-border-gray-300 elm-border-0 elm-border-b elm-border-solid"
        >
          <button
            className="focus:elm-outline-none elm-bg-transparent elm-px-6 elm-pointer-events-auto elm-border-0 elm-cursor-pointer elm-flex elm-font-sans elm-justify-start elm-no-underline elm-py-4 elm-text-dd-gray-900 elm-text-lg elm-transition elm-w-full lg:hover:elm-text-dd-blue-800"
            onClickCapture={onClickOnLogout}
            title={logout.label}
            aria-label={logout.label}
          >
            {logout.label}
          </button>
        </li>
      </ul>
    </div>
  );
}
